import { DiscountGroup, GroupOption, ProductGroupCount } from '@retail/products/types';
import { SupplierBase } from '@retail/suppliers/types';
import { ItemIdentifiersFilter } from './itemIdentifiers';

export const FILTER_CONFIG = {
  supplierNumbers: { urlKey: 'supplierNumbers', type: 'list' },
  supplierNumber: { urlKey: 'supplierNumber', type: 'string' },
  discountGroupCodes: { urlKey: 'discountGroupCodes', type: 'list' },
  discountGroupCode: { urlKey: 'discountGroupCode', type: 'string' },
  productGroupNumbers: { urlKey: 'productGroupNumbers', type: 'list' },
  productGroupNumber: { urlKey: 'productGroupNumber', type: 'string' },
  searchQuery: { urlKey: 'searchQuery', type: 'string' },
  itemSearchQuery: { urlKey: 'itemSearchQuery', type: 'string' },
  subAssortmentIds: { urlKey: 'subAssortmentIds', type: 'list' },
  hierarchyLevel: { urlKey: 'hierarchyLevel', type: 'string' },
  conditionState: { urlKey: 'conditionState', type: 'string' },
  assortmentType: { urlKey: 'assortmentType', type: 'string' },
  addedBy: { urlKey: 'addedBy', type: 'string' },
  purchasePrice: { urlKey: 'purchasePrice', type: 'group' },
  assortmentView: { urlKey: 'assortmentView', type: 'string', default: 'ASSORTMENT_FULL' },
  discontinuedItem: { urlKey: 'discontinuedItem', type: 'group' },
  inAssortment: { urlKey: 'inAssortment', type: 'group' },
  fromMainSupplier: { urlKey: 'fromMainSupplier', type: 'boolean' },
  validFrom: { urlKey: 'validFrom', type: 'string' },
  validTo: { urlKey: 'validTo', type: 'string' },
  activeItems: { urlKey: 'activeItems', type: 'group' },
  forApproval: { urlKey: 'forApproval', type: 'boolean' },
  includeExpiredSubAssortments: { urlKey: 'includeExpiredSubAssortments', type: 'boolean' },
  inStock: { urlKey: 'inStock', type: 'group' },
  isTagged: { urlKey: 'isTagged', type: 'group' },
  isUnhandled: { urlKey: 'isHandled', type: 'boolean' },
  changeTrigger: { urlKey: 'changeTrigger', type: 'string', default: undefined },
  purchasePriceChangeRange: { urlKey: 'purchasePriceChangeRange', type: 'range' },
  salesPriceChangeRange: { urlKey: 'salesPriceChangeRange', type: 'range' },
  covergeDegreeChangeRange: { urlKey: 'coverageDegreeChangeRange', type: 'range' },

  supplierModal: { urlKey: 'supplierModal', type: 'list' },
  discountGroupModal: { urlKey: 'discountGroupModal', type: 'list' },
  productGroupModal: { urlKey: 'productGroupModal', type: 'list' },
  searchQueryModal: { urlKey: 'searchQueryModal', type: 'string' },

  itemIdentifiers: { urlKey: 'itemIdentifiers', type: 'itemIdentifiers', storage: 'memory' },
} as const;

type Config = typeof FILTER_CONFIG;
export type FilterKey = keyof Config;

/** Filters kept in memory per pathname instead of in the URL */
export type MemoryFilterKey = {
  [K in FilterKey]: Config[K] extends { storage: 'memory' } ? K : never;
}[FilterKey];

export const isMemoryFilterKey = (key: FilterKey): key is MemoryFilterKey =>
  'storage' in FILTER_CONFIG[key];

export type AllFilters = {
  [K in FilterKey]: Config[K]['type'] extends 'list'
    ? string[]
    : Config[K]['type'] extends 'range'
    ? string[]
    : Config[K]['type'] extends 'boolean'
    ? boolean
    : Config[K]['type'] extends 'group'
    ? GroupOption
    : Config[K]['type'] extends 'itemIdentifiers'
    ? ItemIdentifiersFilter | null
    : Config[K] extends { default: string }
    ? string
    : string | null;
};

export const getUrlKey = (key: FilterKey): string => FILTER_CONFIG[key].urlKey;

export type SelectedFilters<K extends FilterKey> = Pick<AllFilters, K>;

export type SetFilters<K extends FilterKey> = (updates: {
  [P in K]?: AllFilters[P] | undefined;
}) => void;
interface AllFilterObjectsMap {
  supplierNumber: { selectedSupplier: SupplierBase | null };
  supplierNumbers: { selectedSuppliers: SupplierBase[] };
  supplierModal: { selectedSuppliers: SupplierBase[] };
  discountGroupCode: { selectedDiscountGroup: DiscountGroup | null };
  discountGroupCodes: { selectedDiscountGroups: DiscountGroup[] };
  discountGroupModal: { selectedDiscountGroups: DiscountGroup[] };
  productGroupNumber: { selectedProductGroup: ProductGroupCount | null };
  productGroupNumbers: { selectedProductGroups: ProductGroupCount[] };
  productGroupModal: { selectedProductGroups: ProductGroupCount[] };
}

export type FilterObjects<K extends FilterKey> = {
  [P in keyof AllFilterObjectsMap as P extends K
    ? keyof AllFilterObjectsMap[P]
    : never]: P extends keyof AllFilterObjectsMap
    ? AllFilterObjectsMap[P][keyof AllFilterObjectsMap[P]]
    : never;
};
